import BannerComponent from 'components/BannerComponent';
import { IFilterValue } from 'models/request';
import { IFilterDinamicForm } from 'models/response';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import utils from 'utils';
import { INPUT_TYPE_DYNAMIC_FILTER } from 'utils/constants';

const Container = styled.div`
  margin: 90px 30px;
`;

const AssetContainer = styled.div``;

const OptionsContainer = styled.div`
  margin-top: 30px;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
`;

const Label = styled.div`
  text-align: left;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colorPalette.isDarkMode && 'white'};
`;

interface Props {
  filterList: IFilterDinamicForm[];
  idLoggedUser: number;
  filterValues: IFilterValue[];
  setFilterValues: React.Dispatch<React.SetStateAction<IFilterValue[]>>;
  onSearch: (filterValues: IFilterValue[]) => void;
}

const BannerContainer = (props: Props) => {
  const { filterList, idLoggedUser, filterValues, setFilterValues, onSearch } = props;

  const t = utils.intl.useTranslator();
  const { REACT_APP_BUCKET_URL } = process.env;

  const [selectedBannerId, setSelectedBannerId] = useState<number | null>(null);

  useEffect(() => {
    const matchingFilter = filterValues.find((fv) => filterList.some((fl) => fl.idFilter === fv.idFilter));

    if (matchingFilter && selectedBannerId !== matchingFilter.idFilter) {
      setSelectedBannerId(matchingFilter.idFilter);
    }
  }, [filterValues, filterList, selectedBannerId]);

  useEffect(() => {
    const button = document.getElementById('nuova-ricerca');

    const handleClick = () => {
      setSelectedBannerId(null);
    };

    if (button) {
      button.addEventListener('click', handleClick);
    }

    return () => {
      if (button) {
        button.removeEventListener('click', handleClick);
      }
    };
  }, []);

  const handleSelectInputChange = (option: { value: number; label: string }, selectedFilterId: number | null) => {
    if (!selectedFilterId) return;

    const newFilterValues: IFilterValue[] = [...filterValues];

    const currentFilter = filterList.find((el) => el.idFilter === selectedFilterId);
    const filterName = currentFilter?.filterName;
    const idFilter = currentFilter?.idFilter;
    if (!filterName || !idFilter) return;

    // Remove all other banner filters except the current one
    const cleanedFilterValues = newFilterValues.filter((fv) => {
      const correspondingFilter = filterList.find((fl) => fl.idFilter === fv.idFilter);
      return correspondingFilter?.inputType !== INPUT_TYPE_DYNAMIC_FILTER.BANNER || fv.idFilter === idFilter;
    });

    // Find the index of the current filter (after cleanup)
    const currentFilterIndex = cleanedFilterValues.findIndex((fv) => fv.idFilter === idFilter);

    if (option.value === null || option.value === undefined) {
      // If null, remove it if it exists
      if (currentFilterIndex !== -1) {
        cleanedFilterValues.splice(currentFilterIndex, 1);
      }
    } else {
      // Update or add
      const currentData =
        currentFilterIndex !== -1 ? cleanedFilterValues[currentFilterIndex] : { idFilter: idFilter, options: [] };

      const updatedOptions = [...currentData.options];
      const index = updatedOptions.findIndex((opt: any) => opt[filterName[0]] !== undefined);

      if (index !== -1) {
        updatedOptions[index] = { [filterName[0]]: option.value };
      } else {
        updatedOptions.push({ [filterName[0]]: option.value });
      }

      currentData.options = updatedOptions;

      if (currentFilterIndex === -1) {
        cleanedFilterValues.push(currentData);
      } else {
        cleanedFilterValues[currentFilterIndex] = currentData;
      }
    }
    // Apply the new values and trigger search
    setFilterValues(cleanedFilterValues);
    onSearch(cleanedFilterValues);
  };

  const handleBannerSelect = (id: number) => {
    const filter = filterList.find((f) => f.idFilter === id);
    const options = filter?.inputProp?.options?.[idLoggedUser];

    if (!filter) return;

    // Remove the other banner filters from filterValues
    const otherBannerFiltersRemoved = filterValues.filter((fv) => {
      const f = filterList.find((fl) => fl.idFilter === fv.idFilter);
      return f?.inputType !== INPUT_TYPE_DYNAMIC_FILTER.BANNER || fv.idFilter === id;
    });
    // Update the state immediately
    setFilterValues(otherBannerFiltersRemoved);

    if (Array.isArray(options) && options?.length === 1) {
      const selectedOption = options[0] as { value: number; label: string };
      setSelectedBannerId(id);
      handleSelectInputChange({ value: selectedOption.value ?? id, label: selectedOption.label ?? filter.label }, id);
    } else {
      // Otherwise, update only the selected id
      setSelectedBannerId((prev) => (prev === id ? null : id));
    }
  };

  const selectedBannerOptions = filterList.find((el) => el.idFilter === selectedBannerId)?.inputProp?.options?.[
    idLoggedUser
  ];

  return (
    <Container>
      <AssetContainer>
        <Label>{t('assets')}</Label>
        <CardContainer>
          {filterList.map((filter) => {
            return (
              <BannerComponent
                key={filter.idFilter}
                bannerTitle={filter.label}
                imageUrl={`${REACT_APP_BUCKET_URL}/${filter.inputProp?.imgFileName}`}
                onSelection={() => handleBannerSelect(filter.idFilter)}
                isSelected={selectedBannerId === filter.idFilter}
              />
            );
          })}
        </CardContainer>
      </AssetContainer>
      {Array.isArray(selectedBannerOptions) && selectedBannerOptions.length > 1 && (
        <OptionsContainer>
          <Label>{t('banner')}</Label>
          <CardContainer>
            {(selectedBannerOptions as { value: number; label: string; imgFileName: string }[]).map((el) => {
              const currentFilterValue = filterValues.find((fv) => fv.idFilter === selectedBannerId);
              const isSelected =
                currentFilterValue?.options?.some((opt) => Object.values(opt).includes(el.value)) ?? false;
              return (
                <BannerComponent
                  key={el.value}
                  bannerTitle={el.label}
                  imageUrl={`${REACT_APP_BUCKET_URL}/${el.imgFileName}`}
                  onSelection={() => handleSelectInputChange(el, selectedBannerId)}
                  isSelected={isSelected}
                />
              );
            })}
          </CardContainer>
        </OptionsContainer>
      )}
    </Container>
  );
};

export default BannerContainer;
